import SingleField from "@/components/Sections/SingleField"
import {
  type PeopleJsonldReadItemPeople,
  type PeoplePatchPeopleInput,
  type PeoplePostPeopleInputJsonld,
  getGetPeopleQueryKey,
  getGetPeoplesQueryKey,
  usePatchPeople,
  usePostPeople,
} from "@/networking/api"
import { Box, Button, Collapse, Flex, FormControl, Input, useDisclosure } from "@chakra-ui/react"
import { useForm } from "react-hook-form"
import { FormattedMessage, useIntl } from "react-intl"

import { ButtonWithIcon } from "@/components/Buttons/ButtonWithIcon"
import FormattedErrorMessage from "@/components/FormattedErrorMessage/FormattedErrorMessage"
import { CheckIcon, MinusIcon, PlusIcon } from "@/components/Icons"
import { MultipleField, MultipleFieldColumn } from "@/components/Sections/MultipleField"
import { useGenericErrorToast } from "@/hooks/useGenericErrorToast"
import { fieldRenderForSmallField } from "@/theme/custom"
import { useUnsetFocus } from "@/utils/dom-utils"
import { isBlank, isNotBlank } from "@/utils/ts-utils"
import { useQueryClient } from "@tanstack/react-query"
import { isValidPhoneNumber } from "react-phone-number-input"
import { useAddOrEditPeopleStepFormContext } from "../../context/AddOrEditPeopleFormStepContext"
import type { AddPeopleContacDetailsType } from "../../helpers/AddPeopleData"

type Props = { onClose: (people?: PeopleJsonldReadItemPeople) => void }

export const ContactDetailsStep: React.FC<Props> = ({ onClose }) => {
  const intl = useIntl()

  const { formStep1State, formStep2State, setFormStep2State } = useAddOrEditPeopleStepFormContext()
  const {
    // control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AddPeopleContacDetailsType>({
    defaultValues: {
      ...formStep2State,
    },
  })

  const { isOpen: isMoreFieldsOpen, onToggle: onMoreFieldsToggle } = useDisclosure()
  const { isOpen: isSocialsOpen, onToggle: onSocialsToggle } = useDisclosure()

  const is_edit = "id" in formStep1State && isNotBlank((formStep1State.id as string) ?? "")

  // const { defaulCollaborator: collaborator } = useGetUserInfos()

  const onSubmit = (data: AddPeopleContacDetailsType) => {
    const newFormStep2State = {
      ...formStep2State,
      ...data,
      email: (data.email ?? formStep2State.email ?? "").trim(),
      emailFallback: (data.emailFallback ?? formStep2State.emailFallback ?? "").trim(),
    }
    setFormStep2State(newFormStep2State)

    saveForm(newFormStep2State)
  }

  // Saving process

  const queryClient = useQueryClient()
  const genericErrorToast = useGenericErrorToast()
  const { mutate: mutatePost, isLoading: isLoadingPost } = usePostPeople()
  const { mutate: mutatePatch, isLoading: isLoadingPatch } = usePatchPeople()
  useUnsetFocus(isLoadingPost || isLoadingPatch)

  const saveForm = (newFormStep2State: AddPeopleContacDetailsType) => {
    // Get back data from all steps
    const data = {
      ...formStep1State,
      contactDetails: [newFormStep2State],
    }
    setFormStep2State(newFormStep2State)

    if (is_edit) {
      mutatePatch(
        { id: formStep1State.id as string, data: data as PeoplePatchPeopleInput },
        {
          onSuccess: data => {
            void queryClient.invalidateQueries({ queryKey: getGetPeoplesQueryKey() })
            void queryClient.invalidateQueries({
              queryKey: getGetPeopleQueryKey(formStep1State.id as string),
            })
            onClose?.(data as PeopleJsonldReadItemPeople)
          },
          onError: genericErrorToast,
        }
      )
    } else {
      mutatePost(
        { data: data as PeoplePostPeopleInputJsonld },
        {
          onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: getGetPeoplesQueryKey() })
            onClose?.()
          },
          onError: genericErrorToast,
        }
      )
    }
  }

  const waitBeforeNextStep = false // No reflabel to load ?

  return (
    <Box as="form" onSubmit={handleSubmit(onSubmit)} mb="4rem">
      <Flex direction="column" gap="5" my="8">
        <MultipleField
          label={intl.formatMessage({ id: "client.modalEdit.contactDetailsStep.emails" })}
        >
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.email",
            })}
            required={true}
          >
            <FormControl isInvalid={!!errors.email}>
              <Input
                {...register("email", {
                  required: true,
                  pattern: {
                    value: /^[^@]+@[^@]+\.[^@]+$/,
                    message: "error.email",
                  },
                })}
              />
              <FormattedErrorMessage fieldError={errors.email} />
            </FormControl>
          </MultipleFieldColumn>
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.email2",
            })}
          >
            <FormControl isInvalid={!!errors.emailFallback}>
              <Input
                {...register("emailFallback", {
                  pattern: {
                    value: /^[^@]+@[^@]+\.[^@]+$/,
                    message: "error.email",
                  },
                })}
              />
              <FormattedErrorMessage fieldError={errors.emailFallback} />
            </FormControl>
          </MultipleFieldColumn>
        </MultipleField>

        <MultipleField
          label={intl.formatMessage({ id: "client.modalEdit.contactDetailsStep.phones" })}
        >
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.phone",
            })}
          >
            <FormControl isInvalid={!!errors.phone}>
              <Input
                placeholder={intl.formatMessage({
                  id: "network.form.contactStep.phone.placeholder",
                })}
                {...register("phone", {
                  validate: value =>
                    isBlank(value) || isValidPhoneNumber(value)
                      ? true
                      : "network.form.contactStep.phone.internationalError",
                })}
              />
              <FormattedErrorMessage fieldError={errors.phone} />
            </FormControl>
          </MultipleFieldColumn>
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.phone2",
            })}
          >
            <FormControl isInvalid={!!errors.phone2}>
              <Input
                placeholder={intl.formatMessage({
                  id: "network.form.contactStep.phone.placeholder",
                })}
                {...register("phone2", {
                  validate: value =>
                    isBlank(value) || isValidPhoneNumber(value)
                      ? true
                      : "network.form.contactStep.phone.internationalError",
                })}
              />
              <FormattedErrorMessage fieldError={errors.phone2} />
            </FormControl>
          </MultipleFieldColumn>
        </MultipleField>

        <MultipleField
          label={intl.formatMessage({ id: "client.modalEdit.contactDetailsStep.mobiles" })}
        >
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.mobile",
            })}
            required={true}
          >
            <FormControl isInvalid={!!errors.mobile}>
              <Input
                placeholder={intl.formatMessage({
                  id: "network.form.contactStep.phone.placeholder",
                })}
                {...register("mobile", {
                  required: true,
                  validate: value =>
                    isBlank(value) || isValidPhoneNumber(value)
                      ? true
                      : "network.form.contactStep.phone.internationalError",
                })}
              />
              <FormattedErrorMessage fieldError={errors.mobile} />
            </FormControl>
          </MultipleFieldColumn>
          <MultipleFieldColumn
            label={intl.formatMessage({
              id: "client.modalEdit.contactDetailsStep.mobile2",
            })}
          >
            <FormControl isInvalid={!!errors.mobile2}>
              <Input
                placeholder={intl.formatMessage({
                  id: "network.form.contactStep.phone.placeholder",
                })}
                {...register("mobile2", {
                  validate: value =>
                    isBlank(value) || isValidPhoneNumber(value)
                      ? true
                      : "network.form.contactStep.phone.internationalError",
                })}
              />
              <FormattedErrorMessage fieldError={errors.mobile2} />
            </FormControl>
          </MultipleFieldColumn>
        </MultipleField>

        <Box mt="0" mb="4">
          <ButtonWithIcon
            onClick={onMoreFieldsToggle}
            icon={isMoreFieldsOpen ? MinusIcon : PlusIcon}
            iconColor="accent"
          >
            <FormattedMessage id="form.displayOtherCT" />
          </ButtonWithIcon>
          <Collapse in={isMoreFieldsOpen} animateOpacity style={{ overflow: "visible" }}>
            <Flex direction="column" gap="5" mt="8">
              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.fax",
                })}
                {...fieldRenderForSmallField}
              >
                <FormControl isInvalid={!!errors.fax}>
                  <Input
                    placeholder={intl.formatMessage({
                      id: "network.form.contactStep.phone.placeholder",
                    })}
                    {...register("fax", {
                      validate: value =>
                        isBlank(value) || isValidPhoneNumber(value)
                          ? true
                          : "network.form.contactStep.phone.internationalError",
                    })}
                  />
                  <FormattedErrorMessage fieldError={errors.fax} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.address3",
                })}
              >
                <FormControl isInvalid={!!errors.address3}>
                  <Input {...register("address3")} />
                  <FormattedErrorMessage fieldError={errors.address3} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.address2",
                })}
              >
                <FormControl isInvalid={!!errors.address2}>
                  <Input {...register("address2")} />
                  <FormattedErrorMessage fieldError={errors.address2} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.address1",
                })}
              >
                <FormControl isInvalid={!!errors.address1}>
                  <Input {...register("address1")} />
                  <FormattedErrorMessage fieldError={errors.address1} />
                </FormControl>
              </SingleField>

              <MultipleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.place",
                })}
              >
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.contactDetailsStep.zipcode",
                  })}
                >
                  <FormControl isInvalid={!!errors.zipcode}>
                    <Input {...register("zipcode")} />
                    <FormattedErrorMessage fieldError={errors.zipcode} />
                  </FormControl>
                </MultipleFieldColumn>
                <MultipleFieldColumn
                  label={intl.formatMessage({
                    id: "client.modalEdit.contactDetailsStep.city",
                  })}
                >
                  <FormControl isInvalid={!!errors.city}>
                    <Input {...register("city")} />
                    <FormattedErrorMessage fieldError={errors.city} />
                  </FormControl>
                </MultipleFieldColumn>
              </MultipleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.country",
                })}
                {...fieldRenderForSmallField}
              >
                <FormControl isInvalid={!!errors.country}>
                  <Input {...register("country")} />
                  <FormattedErrorMessage fieldError={errors.country} />
                </FormControl>
              </SingleField>
            </Flex>
          </Collapse>
        </Box>

        <Box mt="0" mb="4">
          <ButtonWithIcon
            onClick={onSocialsToggle}
            icon={isSocialsOpen ? MinusIcon : PlusIcon}
            iconColor="accent"
          >
            <FormattedMessage id="form.displaySocials" />
          </ButtonWithIcon>
          <Collapse in={isSocialsOpen} animateOpacity style={{ overflow: "visible" }}>
            <Flex direction="column" gap="5" mt="8">
              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.url",
                })}
              >
                <FormControl isInvalid={!!errors.url}>
                  <Input
                    {...register("url", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.url} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.socialFacebook",
                })}
              >
                <FormControl isInvalid={!!errors.socialFacebook}>
                  <Input
                    {...register("socialFacebook", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.socialFacebook} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.socialInstagram",
                })}
              >
                <FormControl isInvalid={!!errors.socialInstagram}>
                  <Input
                    {...register("socialInstagram", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.socialInstagram} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.socialLinkedin",
                })}
              >
                <FormControl isInvalid={!!errors.socialLinkedin}>
                  <Input
                    {...register("socialLinkedin", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.socialLinkedin} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.socialTwitter",
                })}
              >
                <FormControl isInvalid={!!errors.socialTwitter}>
                  <Input
                    {...register("socialTwitter", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.socialTwitter} />
                </FormControl>
              </SingleField>

              <SingleField
                label={intl.formatMessage({
                  id: "client.modalEdit.contactDetailsStep.socialYoutube",
                })}
              >
                <FormControl isInvalid={!!errors.socialYoutube}>
                  <Input
                    {...register("socialYoutube", {
                      pattern: {
                        value: /^https?:\/\/[^\/\.]+\.[^\/\.]+/,
                        message: intl.formatMessage({ id: "error.url" }),
                      },
                    })}
                    placeholder="https://"
                  />
                  <FormattedErrorMessage fieldError={errors.socialYoutube} />
                </FormControl>
              </SingleField>
            </Flex>
          </Collapse>
        </Box>
      </Flex>

      <Flex justifyContent="center" mt={10}>
        <Button
          type="submit"
          onClick={handleSubmit(onSubmit)}
          isLoading={isLoadingPost || isLoadingPatch || waitBeforeNextStep}
        >
          <FormattedMessage id="client.modalEdit.form.validate" />
          <CheckIcon boxSize={4} ml={2} />
        </Button>
      </Flex>
    </Box>
  )
}
